import { html, LitElement, type PropertyValues, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";

import structure from "../../../../bible-reader-data/dist/structure.web.json";
import translationImage from "../assets/images/translation-video.jpg";
import { getQuery, getQueryPathForTranslation } from "../query";
import { ariaBoolean } from "..";
import css from "./bible-navigation-translation-button.css?inline";

@customElement("bible-navigation-translation-button")
export class NavigationTranslationButtonElement extends LitElement {
  static styles = unsafeCSS(css);

  private initialTranslation = getQuery().translation.name;

  @property()
  isExpanded = false;

  private refs = {
    button: createRef<HTMLButtonElement>(),
  };

  private onButtonClick() {
    this.isExpanded = !this.isExpanded;
  }

  private onButtonKeyUp(e: KeyboardEvent) {
    if (e.key === "ArrowDown" && !this.isExpanded) {
      this.isExpanded = true;
    }
    if (e.key === "Escape") {
      this.isExpanded = false;
      this.updateComplete.then(() => {
        this.refs.button.value?.focus();
      });
    }
  }
  private close() {
    this.isExpanded = false;
    this.updateComplete.then(() => {
      this.refs.button.value?.focus();
    });
  }
  private onPickerKeyUp(e: KeyboardEvent) {
    if (e.key === "Escape") {
      this.isExpanded = false;
      this.updateComplete.then(() => {
        this.refs.button.value?.focus();
      });
    }
  }

  render() {
    return html`
      <button
        ${ref(this.refs.button)}
        @click=${this.onButtonClick}
        @keyup=${this.onButtonKeyUp}
        aria-controls="picker"
        aria-expanded=${ariaBoolean(this.isExpanded)}
        aria-label="Translation: ${this.initialTranslation}"
        aria-haspopup="true"
        part="button trigger"
      >
        ${this.initialTranslation}
      </button>
      ${this.isExpanded
        ? html` <div @keyup=${this.onPickerKeyUp}>
            <bible-navigation-menu .trigger=${this}>
              <div class="picker" id="picker" part="picker">
                <div class="picker__header" part="picker-header">
                  <div class="picker__header__title" part="header-title">
                    Choose a Bible Translation
                  </div>
                  <button
                    @click=${this.close}
                    class="picker__header__close"
                    part="picker-header-close"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21.125 17.5781C21.5469 18.0469 21.5469 18.75 21.125 19.1719C20.6562 19.6406 19.9531 19.6406 19.5312 19.1719L14 13.5938L8.42188 19.1719C7.95312 19.6406 7.25 19.6406 6.82812 19.1719C6.35938 18.75 6.35938 18.0469 6.82812 17.5781L12.4062 12L6.82812 6.42188C6.35938 5.95312 6.35938 5.25 6.82812 4.82812C7.25 4.35938 7.95312 4.35938 8.375 4.82812L14 10.4531L19.5781 4.875C20 4.40625 20.7031 4.40625 21.125 4.875C21.5938 5.29688 21.5938 6 21.125 6.46875L15.5469 12L21.125 17.5781Z"
                        fill="#1B1B1B"
                      />
                    </svg>
                  </button>
                  <div class="sections" part="sections">
                    ${structure.translations.map(
                      (section) => html`
                        <div class="section" part="section">
                          <div class="section_title" part="section-title">
                            ${section.title.replace(/-/g, " ")}
                          </div>
                          <div
                            class="section_translations"
                            part="section-translations"
                          >
                            ${section.translations.map(
                              (translation) => html`
                                <a
                                  class="section_translation"
                                  part="section-translation"
                                  href=${getQueryPathForTranslation(
                                    translation.slug,
                                  )}
                                >
                                  <span>${translation.name}</span>
                                  <strong>${translation.full_name}</strong>
                                  <svg
                                    width="16"
                                    height="12"
                                    viewBox="0 0 16 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    part="section-translation-check"
                                  >
                                    <path
                                      d="M14.6875 1.3125C15.0938 1.6875 15.0938 2.34375 14.6875 2.71875L6.6875 10.7188C6.3125 11.125 5.65625 11.125 5.28125 10.7188L1.28125 6.71875C0.875 6.34375 0.875 5.6875 1.28125 5.3125C1.65625 4.90625 2.3125 4.90625 2.6875 5.3125L6 8.59375L13.2812 1.3125C13.6562 0.90625 14.3125 0.90625 14.6875 1.3125Z"
                                      fill="#1B1B1B"
                                    />
                                  </svg>
                                </a>
                              `,
                            )}
                          </div>
                        </div>
                      `,
                    )}
                  </div>
                </div>

                <!-- /Tabs -->
                <div>
                  <bible-video-feature
                    part="bible-video-feature"
                    description="Need help understanding the differences in translations? Watch our video about choosing a Bible translation to learn more."
                    image-url=${translationImage}
                    .video=${{
                      aspectRatio: 9 / 16,
                      muxId: "MBrEUJ7HFq8DU7oU3iKLZANhTmdKeDi2dC17o2kTdQM",
                      title: "Pick Your Translation",
                    }}
                  ></bible-video-feature>
                </div>
              </div>
            </bible-navigation-menu>
          </div>`
        : null}
    `;
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has("isExpanded") && this.isExpanded) {
      this.dispatchEvent(new CustomEvent("open"));
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bible-navigation-translation-button": NavigationTranslationButtonElement;
  }
}
